import { useCallback, useEffect, useState } from 'react';
import './Product.css';
import BasicCli from '../../main/components/basicCli/BasicCli';
import EditModal from '../../main/components/modal/edit/EditModal';
import eFormType from '../../main/assets/enums/eFormType';
import DeleteModal from '../../main/components/modal/delete/DeleteModal';
import { ApiConstants } from '../../main/constants/ApiConstants';
import IProduct from '../../main/interfaces/IProduct';
import { toast } from 'react-toastify';
import ProductCategory from './productCategory/ProductCategory';
import axiosInstance from '../../main/initializers/axios';
import ProductEdit from './productEdit/ProductEdit';
import IGridItems from '../../main/interfaces/IGridItems';
import IPagination from '../../main/interfaces/IPagination';
import GeneralTable from '../../main/components/grid/GeneralTable';
import RightPanel from '../../main/components/rightPanel/RightPanel';
import { useTranslation } from 'react-i18next';

const Product = () => {
  const [products, setProducts] = useState<IProduct[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isModalEditOpen, setIsModalEditOpen] = useState<boolean>(false);
  const [isModalDeleteOpen, setIsModalDeleteOpen] = useState<boolean>(false);
  const [selectedVal, setSelectedVal] = useState<IProduct | null>(null);
  const [approved, setApproved] = useState<boolean>(false);
  const [isCategoryOpen, setCategoryOpen] = useState<boolean>(false);

  const [tableName, setTableName] = useState<string>('Products');
  const [gridItems, setGridItems] = useState<IGridItems[]>([]);
  const [pagination, setPagination] = useState<IPagination>({
    count: 1,
    page: 1,
    itemPerPage: 50,
    itemsPerPage: [10, 20, 50, 100],
  });

  const { i18n } = useTranslation();

  const isTemporaryQuantityColumn = (col: IGridItems) => {
    const entity = String(col?.entity ?? '').toLowerCase();
    const label = String(col?.label ?? '').toLowerCase();

    return entity === 'tempqty' || entity === 'temporaryquantity' || label === 'tempqty' || label === 'temporaryquantity';
  };

  const handleEdit = (item: IProduct) => {
    setTableName('Edit ' + (item.name ?? 'Product'));
    setSelectedVal(item);
    setIsModalEditOpen(true);
  };

  const handleDelete = (item: IProduct) => {
    setSelectedVal(item);
    setIsModalDeleteOpen(true);
  };

  const getProducts = useCallback(
    async (pag?: IPagination, gridOverride?: IGridItems[]) => {
      try {
        const effective = pag ?? pagination;
        const effectiveGrid = gridOverride ?? gridItems ?? [];
        const [res, stockRes] = await Promise.all([
          axiosInstance.post(ApiConstants.Products.listct, {
            gridItems: effectiveGrid,
            pagination: effective,
          }),
          axiosInstance.get(ApiConstants.stock.list),
        ]);

        const items = res.data?.items ?? [];
        const p = res.data?.pagination ?? {};
        const cols = (res.data?.gridItems ?? []).filter((col: IGridItems) => !isTemporaryQuantityColumn(col));
        const stockItems = Array.isArray(stockRes.data) ? stockRes.data : Array.isArray(stockRes.data?.items) ? stockRes.data.items : [];
        const stockByProductId = new Map<number, any>(
          stockItems.map((stock: any) => [Number(stock?.productId ?? stock?.ProductId ?? 0), stock]),
        );

        const normalizedItems = items.map((item: IProduct) => {
          const stock = stockByProductId.get(Number(item.id));
          if (!stock) return item;

          return {
            ...item,
            qty: Number(stock?.quantity ?? stock?.Quantity ?? item.qty ?? 0),
            tempQty: Number(stock?.quantity ?? stock?.Quantity ?? item.tempQty ?? 0),
          };
        });

        setProducts(normalizedItems);
        setGridItems(cols);

        setPagination((prev) => ({
          ...prev,
          count: p.count ?? prev.count,
          page: p.page ?? prev.page,
          itemPerPage: p.itemPerPage ?? prev.itemPerPage,
          itemsPerPage: p.itemsPerPage ?? prev.itemsPerPage,
        }));
      } catch (error) {
        toast.error(i18n.t('errors.failedToFetchProducts'));
      }
    },
    [gridItems, pagination],
  );

  const selectedItemPagination = (pag: IPagination) => {
    setPagination(pag);
    getProducts(pag);
  };

  const deleteDataSet = (id: number) => {
    axiosInstance.delete(ApiConstants.Products.delete + `?id=` + id).then((response) => {
      if (response.status === 200) {
        toast.success(i18n.t('productCategory.deletedSuccessfully'));
        setIsModalDeleteOpen(false);
        getProducts();
      } else {
        toast.error(i18n.t('productCategory.somethingWentWrong'));
      }
    });
  };

  useEffect(() => {
    getProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <BasicCli>
      <div className=" bcPageContent">
        <div className="prContainer">
          <div className="bcPageHeader">
            <h1 className="bcPageTitle">{i18n.t('product.products')}</h1>
            <div className="bcPageHeaderActions">
              <button className="ui-control ui-btn ui-btn--primary" onClick={() => setIsModalOpen(true)} type="button">
                {i18n.t('product.addProduct')}
              </button>

              <button className="ui-control ui-btn ui-btn--secondary" onClick={() => setCategoryOpen(true)} type="button">
                {i18n.t('product.addCategory')}
              </button>
            </div>
          </div>

          {/* Table */}
          <div className=" prTable">
            <GeneralTable
              data={products}
              columns={gridItems}
              gridEntity={2}
              title=""
              hasRp={true}
              selectedItem={(val: any) => setSelectedVal(val)}
              onEdit={handleEdit}
              onDelete={handleDelete}
              pagination={pagination}
              selectedItemPagination={(val: any) => selectedItemPagination(val)}
              onFilterClose={(nextGridItems) => {
                setGridItems(nextGridItems);
                getProducts(undefined, nextGridItems);
              }}
            />
          </div>

          {/* RightPanel */}
          <RightPanel
            isOpen={isCategoryOpen}
            size={50}
            type="right"
            panelContainerClassName="productCategoryPanelShell"
            panelClassName="productCategoryPanel"
            onKill={() => setCategoryOpen(false)}
            backdropClicked={() => setCategoryOpen(false)}
          >
            <ProductCategory header={i18n.t('productCategory.title')} isProductCategory={false} />
          </RightPanel>

          {/* Delete */}
          {isModalDeleteOpen && selectedVal ? (
            <DeleteModal width="500px" handleClose={() => setIsModalDeleteOpen(false)} handleDelete={() => deleteDataSet(selectedVal.id ?? 0)}>
              Doni te fshini <strong className="text-danger">{selectedVal?.name}</strong> Kodi({selectedVal?.code})。
            </DeleteModal>
          ) : null}

          {/* Edit */}
          {isModalEditOpen && selectedVal ? (
            <EditModal
              title={tableName}
              width="80%"
              tempProduct={selectedVal}
              closeModal={() => setIsModalEditOpen(false)}
              onSubmitData={() => setApproved(true)}
              setIsModalOpen={setIsModalEditOpen}
              modalType={eFormType.Edit}
            >
              <ProductEdit
                tempProduct={selectedVal}
                modalType={eFormType.Edit}
                createOrUpdate={approved}
                onSubmitData={() => {
                  getProducts();
                  setApproved(false);
                }}
              />
            </EditModal>
          ) : null}

          {/* New */}
          {isModalOpen ? (
            <EditModal
              title={i18n.t('product.addProduct')}
              width="80%"
              closeModal={() => setIsModalOpen(false)}
              onSubmitData={() => setApproved(true)}
              setIsModalOpen={setIsModalOpen}
              modalType={eFormType.New}
            >
              <ProductEdit
                createOrUpdate={approved}
                modalType={eFormType.New}
                onSubmitData={() => {
                  getProducts();
                  setApproved(false);
                  setIsModalOpen(false);
                }}
              />
            </EditModal>
          ) : null}
        </div>
      </div>
    </BasicCli>
  );
};

export default Product;
